import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft, Search, ExternalLink, CheckCircle2, XCircle, Clock, Ban, Eye, Download,
} from "lucide-react";
import CoachActionDrawer from "@/components/CoachActionDrawer";

const STATUS_META = {
  draft: { label: "Draft", cls: "bg-orange-100 text-orange-700", icon: Clock },
  sent: { label: "Sent", cls: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  failed: { label: "Failed", cls: "bg-rose-100 text-rose-700", icon: XCircle },
  discarded: { label: "Cancelled", cls: "bg-zinc-100 text-zinc-600", icon: Ban },
};

const ENTITY_ROUTE = {
  lead: (id) => `/app/leads/${id}`,
  estimate: (id) => `/app/estimates/${id}`,
  customer: (id) => `/app/customers/${id}`,
  invoice: (id) => `/app/invoices/${id}`,
  job: (id) => `/app/jobs/${id}`,
};

export default function CoachApprovalLog() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [channel, setChannel] = useState(sp.get("channel") || "");
  const [action_type, setActionType] = useState("");
  const [origin, setOrigin] = useState(sp.get("origin") || "");
  const [sentiment, setSentiment] = useState(sp.get("sentiment") || "");
  const [intent, setIntent] = useState(sp.get("intent") || "");
  const [hasReplyOnly, setHasReplyOnly] = useState(sp.get("has_reply_only") === "true");
  const [weekKey, setWeekKey] = useState(sp.get("week_key") || "");
  const [from_, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [opened, setOpened] = useState(null);
  const [repliesFor, setRepliesFor] = useState(null);
  const wrapFiltersActive = weekKey || sentiment || intent || hasReplyOnly;

  const clearWrapFilters = () => {
    setWeekKey(""); setSentiment(""); setIntent(""); setHasReplyOnly(false);
    setSp({});
  };

  const exportCsv = async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (status) params.set("status", status);
    if (channel) params.set("channel", channel);
    if (action_type) params.set("action_type", action_type);
    if (from_) params.set("from_", from_);
    if (to) params.set("to", to);
    try {
      const resp = await api.get(`/business-coach/actions/export.csv?${params.toString()}`,
        { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      const range = from_ && to ? `-${from_}-to-${to}` : `-${today}`;
      a.download = `leadforge-coach-actions${range}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // Fallback: use fetch with cookies
      window.open(`${API}/business-coach/actions/export.csv?${params.toString()}`, "_blank");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (status) params.set("status", status);
      if (channel) params.set("channel", channel);
      if (action_type) params.set("action_type", action_type);
      if (from_) params.set("from_", from_);
      if (to) params.set("to", to);
      if (origin) params.set("origin", origin);
      // Reply-related filters — send to backend for server-side scoping but
      // also apply client-side so pre-classification rows still filter.
      const { data } = await api.get(`/business-coach/actions?${params.toString()}`);
      let filtered = data;
      if (hasReplyOnly) filtered = filtered.filter(r => (r.reply_count || 0) > 0);
      if (sentiment) filtered = filtered.filter(r => r.last_reply_sentiment === sentiment);
      if (intent) filtered = filtered.filter(r => r.last_reply_intent === intent);
      setRows(filtered);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, channel, action_type, from_, to, origin, sentiment, intent, hasReplyOnly]);

  const drafted = rows.filter(r => r.status === "draft").length;
  const sent = rows.filter(r => r.status === "sent").length;
  const failed = rows.filter(r => r.status === "failed").length;

  return (
    <div className="p-6 md:p-8 max-w-7xl" data-testid="coach-approval-log">
      <button
        onClick={() => nav("/app/coach")}
        data-testid="approval-log-back"
        className="flex items-center gap-1 text-sm text-zinc-500 hover:text-[#FF5A00] mb-3"
      >
        <ArrowLeft className="w-4 h-4"/> Back to Coach
      </button>
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">Coach Approval Log</div>
          <h1 className="font-heading text-3xl font-bold">Every Coach-approved action</h1>
        </div>
        <div className="text-sm text-zinc-500">{rows.length} record{rows.length === 1 ? "" : "s"}</div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Kpi label="Drafts" value={drafted}/>
        <Kpi label="Sent" value={sent}/>
        <Kpi label="Failed" value={failed}/>
        <Kpi label="Cancelled" value={rows.filter(r => r.status === "discarded").length}/>
      </div>

      <Card className="p-4 mb-4">
        {wrapFiltersActive && (
          <div className="bg-orange-50 border border-orange-200 text-xs text-orange-900 rounded-md p-2 mb-3 flex items-center justify-between" data-testid="wrap-filters-banner">
            <div>
              Filtered from Friday Wrap:{" "}
              {weekKey && <><b>{weekKey}</b> · </>}
              {sentiment && <>sentiment=<b>{sentiment}</b> · </>}
              {intent && <>intent=<b>{intent.replace(/_/g, " ")}</b> · </>}
              {hasReplyOnly && <>replies only</>}
            </div>
            <button onClick={clearWrapFilters} className="text-[#FF5A00] hover:underline" data-testid="clear-wrap-filters">
              Clear Wrap Filters
            </button>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div className="md:col-span-2 relative">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-zinc-400"/>
            <Input
              className="pl-8" placeholder="Search customer, subject or body"
              value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              data-testid="approval-log-search"
            />
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)}
                  data-testid="approval-log-status"
                  className="border border-zinc-200 rounded-md px-3 py-2 text-sm">
            <option value="">All statuses</option>
            <option value="draft">Drafts</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
            <option value="discarded">Cancelled</option>
          </select>
          <select value={channel} onChange={(e) => setChannel(e.target.value)}
                  data-testid="approval-log-channel"
                  className="border border-zinc-200 rounded-md px-3 py-2 text-sm">
            <option value="">All channels</option>
            <option value="email">Email</option>
            <option value="sms">SMS</option>
          </select>
          <select value={action_type} onChange={(e) => setActionType(e.target.value)}
                  data-testid="approval-log-action-type"
                  className="border border-zinc-200 rounded-md px-3 py-2 text-sm">
            <option value="">All types</option>
            <option value="follow_up_estimate">Estimate follow-up</option>
            <option value="follow_up_lead">Lead follow-up</option>
            <option value="collect_invoice">Invoice collection</option>
            <option value="reengage_customer">Re-engagement</option>
          </select>
          <select value={origin} onChange={(e) => setOrigin(e.target.value)}
                  data-testid="approval-log-origin"
                  className="border border-zinc-200 rounded-md px-3 py-2 text-sm">
            <option value="">All origins</option>
            <option value="auto_reply">Auto-drafted from reply</option>
          </select>
          <Button onClick={load} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white"
                  data-testid="approval-log-apply">
            Apply
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-3 text-xs text-zinc-500">
          <select value={sentiment} onChange={(e) => setSentiment(e.target.value)}
                  data-testid="approval-log-sentiment"
                  className="border border-zinc-200 rounded-md px-2 py-1 text-xs">
            <option value="">All sentiment</option>
            <option value="positive">Positive</option>
            <option value="neutral">Neutral</option>
            <option value="negative">Negative</option>
            <option value="uncertain">Uncertain</option>
          </select>
          <select value={intent} onChange={(e) => setIntent(e.target.value)}
                  data-testid="approval-log-intent"
                  className="border border-zinc-200 rounded-md px-2 py-1 text-xs">
            <option value="">All intent</option>
            {["interested","appointment_request","estimate_request","question","objection","not_interested","stop_request","wrong_person","other"].map(i =>
              <option key={i} value={i}>{i.replace(/_/g, " ")}</option>)}
          </select>
          <label className="flex items-center gap-1">
            <input type="checkbox" checked={hasReplyOnly}
                   onChange={(e) => setHasReplyOnly(e.target.checked)}
                   data-testid="approval-log-has-reply-only"/>
            <span>Replies only</span>
          </label>
          <span>From</span>
          <Input type="date" value={from_} onChange={(e) => setFrom(e.target.value)}
                 data-testid="approval-log-from" className="w-40 text-xs"/>
          <span>To</span>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                 data-testid="approval-log-to" className="w-40 text-xs"/>
          <div className="flex-1"/>
          <Button variant="outline" size="sm" onClick={exportCsv}
                  data-testid="approval-log-export">
            <Download className="w-3 h-3 mr-1"/> Export CSV
          </Button>
        </div>
      </Card>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="approval-log-table">
          <thead className="bg-zinc-50 border-b border-zinc-200 text-xs uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">When</th>
              <th className="text-left px-4 py-2">Type</th>
              <th className="text-left px-4 py-2">Channel</th>
              <th className="text-left px-4 py-2">Customer</th>
              <th className="text-left px-4 py-2">Approved by</th>
              <th className="text-left px-4 py-2">Delivery</th>
              <th className="text-left px-4 py-2">Replies</th>
              <th className="text-left px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="text-center py-6 text-zinc-500">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="text-center py-6 text-zinc-500" data-testid="approval-log-empty">
                No records match these filters.
              </td></tr>
            )}
            {!loading && rows.map((r) => {
              const m = STATUS_META[r.status] || { label: r.status, cls: "bg-zinc-100 text-zinc-700", icon: Clock };
              const Icon = m.icon;
              const link = r.entity_id && ENTITY_ROUTE[r.entity_type]?.(r.entity_id);
              return (
                <tr key={r.id} className="border-b border-zinc-100 hover:bg-orange-50/30"
                    data-testid={`approval-log-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${m.cls}`}>
                      <Icon className="w-3 h-3"/> {m.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-600">
                    {(r.sent_at || r.approved_at || r.drafted_at || "").replace("T", " ").slice(0, 16)}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {(r.kind || "").replace(/_/g, " ")}
                    {r.origin === "auto_reply" && (
                      <span
                        className="ml-2 inline-flex items-center gap-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 border border-orange-200"
                        title={`Auto-drafted from ${r.source_reply_intent || "reply"}`}
                        data-testid={`approval-log-auto-reply-badge-${r.id}`}
                      >
                        ↩ Auto-drafted from reply
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs uppercase">{r.channel}</td>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium">{r.target_name || "—"}</div>
                    <div className="text-xs text-zinc-500">{r.target_email || r.target_phone || ""}</div>
                  </td>
                  <td className="px-4 py-3 text-xs">{r.approved_by_name || "—"}</td>
                  <td className="px-4 py-3 text-xs">
                    {r.delivery_result?.ok === true && "delivered to provider"}
                    {r.delivery_result?.ok === false && (r.reason || "failed")}
                    {!r.delivery_result && "—"}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <ReplyCell r={r} onOpen={() => setRepliesFor(r)}/>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <div className="flex items-center gap-2">
                      <button onClick={() => setOpened(r.id)} className="text-[#FF5A00] hover:underline flex items-center gap-1"
                              data-testid={`approval-log-view-${r.id}`}>
                        <Eye className="w-3 h-3"/> View
                      </button>
                      {link && (
                        <a href={link} target="_blank" rel="noreferrer"
                           className="text-zinc-500 hover:text-[#FF5A00] flex items-center gap-1">
                          <ExternalLink className="w-3 h-3"/> Record
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {opened && (
        <CoachActionDrawer
          initial={{ action_id: opened }}
          onClose={() => setOpened(null)}
          onDone={() => { setOpened(null); load(); }}
        />
      )}
      {repliesFor && (
        <RepliesDrawer action={repliesFor} onClose={() => setRepliesFor(null)}/>
      )}
    </div>
  );
}

const SENTIMENT_CLS = {
  positive: "bg-emerald-100 text-emerald-700",
  negative: "bg-rose-100 text-rose-700",
  neutral: "bg-zinc-100 text-zinc-600",
};

function ReplyCell({ r, onOpen }) {
  const count = r.reply_count || 0;
  if (count === 0) return <span className="text-zinc-400" data-testid={`reply-cell-empty-${r.id}`}>No replies</span>;
  const sent = r.last_reply_sentiment || "neutral";
  const intent = r.last_reply_intent;
  return (
    <button
      onClick={onOpen}
      data-testid={`reply-cell-open-${r.id}`}
      className="inline-flex items-center gap-1 hover:underline"
    >
      <span className="font-semibold text-[#FF5A00]">{count}</span>
      <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${SENTIMENT_CLS[sent] || SENTIMENT_CLS.neutral}`}>
        {sent}
      </span>
      {intent && <span className="text-[10px] text-zinc-500">· {intent.replace(/_/g, " ")}</span>}
    </button>
  );
}

function RepliesDrawer({ action, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/business-coach/actions/replies?action_id=${action.id}`)
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [action.id]);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose} data-testid="coach-replies-drawer">
      <div className="w-full sm:w-[560px] bg-white h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 border-b border-zinc-200 bg-white px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono-accent uppercase tracking-widest text-[#FF5A00]">Coach replies</div>
            <div className="font-heading text-xl font-bold">{action.target_name || "Reply thread"}</div>
            <div className="text-xs text-zinc-500 mt-0.5">
              {(action.kind || "").replace(/_/g, " ")} · {action.channel}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded" data-testid="coach-replies-close">×</button>
        </div>
        <div className="p-5 space-y-4">
          {loading && <div className="text-sm text-zinc-500">Loading…</div>}
          {!loading && (data?.count === 0) && (
            <div className="text-sm text-zinc-500" data-testid="coach-replies-empty">No replies yet.</div>
          )}
          {(data?.replies || []).map((rp) => (
            <div key={rp.id} className="border border-zinc-200 rounded-lg p-3" data-testid={`coach-reply-${rp.id}`}>
              {rp.action && (
                <div className="text-xs bg-orange-50 border border-orange-100 rounded p-2 mb-2">
                  <div className="text-[10px] uppercase tracking-wider text-[#FF5A00] mb-1">Coach approved</div>
                  {rp.action.subject && <div><b>{rp.action.subject}</b></div>}
                  <div className="whitespace-pre-wrap font-mono text-[11px] mt-1">{(rp.action.body || "").slice(0, 200)}{(rp.action.body || "").length > 200 ? "…" : ""}</div>
                  {rp.action.entity_type && rp.action.entity_id && (
                    <a href={`/app/${rp.action.entity_type}s/${rp.action.entity_id}`} className="text-[#FF5A00] hover:underline text-xs mt-1 inline-block">
                      Open {rp.action.entity_type} →
                    </a>
                  )}
                </div>
              )}
              <div className="text-xs text-zinc-500 mb-1">
                {rp.channel} · {rp.received_at?.replace("T", " ").slice(0, 16)} · from {rp.from_address}
              </div>
              <div className="whitespace-pre-wrap text-sm">{rp.body}</div>
              <div className="mt-2 flex items-center gap-2 text-[10px]">
                {rp.sentiment && (
                  <span className={`uppercase tracking-wider px-1.5 py-0.5 rounded-full ${SENTIMENT_CLS[rp.sentiment] || SENTIMENT_CLS.neutral}`}>{rp.sentiment}</span>
                )}
                {rp.intent && <span className="text-zinc-500">intent: {rp.intent.replace(/_/g, " ")}</span>}
                {rp.action?.target_customer_id && (
                  <a href={`/app/customers/${rp.action.target_customer_id}`} className="text-[#FF5A00] hover:underline">Customer →</a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono-accent">{label}</div>
      <div className="font-heading text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}
